import { GetInferredFromRaw } from '@thinknimble/tn-models'
import { z } from 'zod'

export const jobStatusEnum = {
  QUEUED: 'queued',
  IN_PROGRESS: 'in_progress',
  DONE: 'done',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const

export type JobStatusValues = (typeof jobStatusEnum)[keyof typeof jobStatusEnum]

export const jobStatusLabelMap = {
  [jobStatusEnum.QUEUED]: 'Queued',
  [jobStatusEnum.IN_PROGRESS]: 'In Progress',
  [jobStatusEnum.DONE]: 'Done',
  [jobStatusEnum.FAILED]: 'Failed',
  [jobStatusEnum.CANCELLED]: 'Cancelled',
}

export const taskTypeEnum = {
  TRA_SUBMISSION: 'tra_submission',
  SUBMISSION_RECEIPT: 'submission_receipt',
  STATEMENT_PROCESSING: 'statement_processing',
  STATEMENT_RECONCILIATION: 'statement_reconciliation',
  GENERATE_QUERY_VECTOR: 'generate_query_vector',
  GENERATE_FILTERS: 'generate_filters',
  EXPORT_REPORT: 'export_report',
  RECEIPT_ANALYSIS: 'receipt_analysis',
  CUENTA_DE_COBRO_GENERATION: 'cuenta_de_cobro_generation',
  BULK_OPERATION: 'bulk_operation',
} as const

export type TaskTypeValues = (typeof taskTypeEnum)[keyof typeof taskTypeEnum]

export const jobFailureReasonEnum = {
  INSUFFICIENT_BALANCE: 'insufficient_balance',
} as const

export type JobFailureReasonValues =
  (typeof jobFailureReasonEnum)[keyof typeof jobFailureReasonEnum]

export const asyncJobShape = {
  id: z.string().uuid(),
  taskType: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  label: z.string(),
  status: z.enum(jobStatusEnum),
  phase: z.string(),
  progressCurrent: z.number(),
  progressTotal: z.number(),
  errorMessage: z.string(),
  failureReason: z.string(),
  result: z.any().nullable().optional(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  created: z.string(),
}

export const asyncJobFilterShape = {
  status: z.string(),
}

/**
 * The subset of {@link asyncJobShape} that the `ws/jobs/` broadcast actually sends
 * (see server `broadcast_job_event`). The tn-models ws consumer validates and
 * camelCases incoming payloads against this shape; the tracker hydrates the result
 * into a full {@link AsyncJob} with defaults for the fields the broadcast omits.
 */
export const jobEventShape = {
  id: z.string(),
  status: z.enum(jobStatusEnum),
  taskType: z.string(),
  entityId: z.string(),
  label: z.string(),
  errorMessage: z.string().optional().default(''),
  failureReason: z.string().optional().default(''),
  result: z.any().nullable().optional(),
}

export type AsyncJob = GetInferredFromRaw<typeof asyncJobShape>
export type AsyncJobFilter = GetInferredFromRaw<typeof asyncJobFilterShape>
export type JobEvent = GetInferredFromRaw<typeof jobEventShape>

/**
 * Build an optimistic in-progress job to drop into the store the instant a
 * dispatch returns its async job id — so spinners/widgets light up immediately
 * instead of waiting for the first safety poll. The real job (same `id`) later
 * replaces it via the websocket terminal event or the poll's reconcile, so this
 * placeholder is always superseded, never stranded.
 */
export function buildOptimisticJob({
  id,
  taskType,
  entityId,
  label,
  entityType = '',
}: {
  id: string
  taskType: string
  entityId: string
  label: string
  entityType?: string
}): AsyncJob {
  const now = new Date().toISOString()
  return {
    id,
    taskType,
    entityType,
    entityId,
    label,
    status: jobStatusEnum.IN_PROGRESS,
    phase: '',
    progressCurrent: 0,
    progressTotal: 0,
    errorMessage: '',
    failureReason: '',
    result: null,
    startedAt: now,
    completedAt: null,
    created: now,
  }
}
